import { Optional } from '@angular/core';
import { Router } from '@angular/router';
import { isString } from 'lodash-es';

import { from, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { NavigationLink } from '@stylehaven/components';
import { defaultNavigationOptions, NavigationOptions } from '../../entities/navigation/navigation-options.entity';
import { NavigationQuery } from '../../entities/navigation/navigation-query.entity';
import { ApiFragment } from '../../record/entities/api-fragment.entity';
import { RecordService } from '../../record/services/record.service';
import { VersioningService } from '../../record/services/versioning.service';
import { ToastHelper } from '../../toast/service/toast.helper';

export abstract class RecordNavigationService {
    constructor(
        protected recordService: RecordService,
        @Optional() private versioningService: VersioningService,
        protected router: Router,
        protected toastHelper: ToastHelper
    ) {}

    abstract navigateTo(record: ApiFragment, navigationOptions?: NavigationOptions): Observable<boolean>;

    getNavigationLink(navigationQuery: NavigationQuery<ApiFragment | string>): NavigationLink {
        const path = ['records', this.getRecordId(navigationQuery)];
        return {
            route: this.router.createUrlTree(path).toString(),
            target: navigationQuery.navigationOptions?.target,
            extras: {
                state: navigationQuery.navigationOptions?.state,
                queryParams: navigationQuery.navigationOptions?.queryParams
            }
        };
    }

    private getRecordId(navigationQuery: NavigationQuery<ApiFragment | string>): string {
        if (isString(navigationQuery.record)) {
            return navigationQuery.record;
        }
        return navigationQuery.record.getRecordId();
    }

    navigateById(id: string, navigationOptions: NavigationOptions = defaultNavigationOptions): Observable<boolean> {
        return this.recordService.getByRecordId(id).pipe(
            switchMap(record => this.navigateTo(record, navigationOptions)),
            catchError(e => {
                console.error(`Error occurred while navigating to record ${id}`);
                return this.handleNavigationError(e, navigationOptions);
            })
        );
    }

    navigateByIdAndVersion(
        id: string,
        version: string,
        navigationOptions: NavigationOptions = defaultNavigationOptions
    ): Observable<boolean> {
        return this.versioningService.getRecordVersion(id, version).pipe(
            switchMap(record => this.navigateTo(record, navigationOptions)),
            catchError(err => this.handleNavigationError(err, navigationOptions))
        );
    }

    protected createRecordNavigationLink(
        navigationQuery: NavigationQuery<ApiFragment>,
        urlParts: string[]
    ): NavigationLink {
        return {
            route: this.router.createUrlTree(urlParts).toString(),
            extras: {
                state: {
                    ...navigationQuery.navigationOptions?.state,
                    record: navigationQuery.record.metadata
                }
            }
        };
    }

    protected navigateToLink(navLink: NavigationLink | null): Observable<boolean> {
        if (navLink === null) {
            return of(false);
        }
        if (navLink.target === '_blank') {
            window.open(navLink.route, '_blank')!.focus();
            return of(true);
        }
        return from(this.router.navigate([navLink.route], navLink.extras));
    }

    protected handleNavigationError(err: any, options: NavigationOptions): Observable<boolean> {
        console.error(err);
        if (options.hideToasts !== true) {
            this.toastHelper.createFailToast('toasts.record-navigation.FAIL');
        }
        return of(false);
    }
}
